/**
 * Public API client — mirrors the shape of the Python SDK.
 *
 * The chat UI, model selector, insights dashboard, and audit-log view all
 * call into this module. Every request goes to `https://api.aridel.ai/v1/*`
 * with the user's `aridel_live_*` key from sessionStorage.
 *
 * The shape intentionally tracks `packages/sdk/src/aridel/resources/*`:
 * a request that works here is the same shape a third-party SDK consumer
 * would send. That's the architectural commitment — the web app dogfoods
 * the public API.
 */

import { fetchAuthSession } from 'aws-amplify/auth';

import { API_KEY_STORAGE_KEY, PUBLIC_API_BASE_URL } from './config';

// Normalize once so a base with/without a trailing slash both work; paths
// below always start with `/`. The base must already include the `/v1`
// prefix (config default + the deploy env provide it).
const BASE = PUBLIC_API_BASE_URL.replace(/\/$/, '');

export class AridelAuthError extends Error {}
export class AridelApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
  }
}

/**
 * Bearer token for the public API. Prefers the user's `aridel_live_*` key
 * (seeded into sessionStorage at sign-in), but falls back to the Cognito JWT
 * — the public API authorizer (`shared/auth.py`) accepts either. The fallback
 * is what makes the web app work for returning users whose raw key can't be
 * re-shown (it's stored only as a hash server-side).
 */
async function authToken(): Promise<string> {
  const key = sessionStorage.getItem(API_KEY_STORAGE_KEY);
  if (key) return key;
  const session = await fetchAuthSession();
  const jwt = session.tokens?.idToken?.toString();
  if (jwt) return jwt;
  throw new AridelAuthError(
    'No Aridel API key or Cognito session available. Sign in first.',
  );
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const url = `${BASE}${path}`;
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${await authToken()}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const resp = await fetch(url, { ...init, headers });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    const err = body?.error ?? {};
    throw new AridelApiError(
      err.message ?? `${resp.status} ${resp.statusText}`,
      resp.status,
      err.code,
    );
  }
  return resp.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// chat.completions
// ---------------------------------------------------------------------------

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string | Array<{ type: string; [key: string]: unknown }>;
  name?: string;
  tool_call_id?: string;
}

export interface ChatCompletionRequest {
  model: string;
  messages: ChatMessage[];
  temperature?: number;
  top_p?: number;
  max_tokens?: number;
  stream?: boolean;
  tools?: unknown[];
  tool_choice?: unknown;
  response_format?: unknown;
  plugins?: unknown[];
  model_pool?: string[];
  provider_keys?: Record<string, string>;
  [key: string]: unknown;
}

export interface Usage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_usd?: number;
}

/**
 * Routing decision returned for `model: "auto"` requests. The chosen model is
 * the top-level `model` field on the response/chunk — NOT inside this object.
 * Shape mirrors the public API (`_public_api_impl.py`) and the SDK.
 */
export interface RoutingInfo {
  benchmark?: string;
  confidence?: number;
  complexity_tier?: string;
  ema_active?: boolean;
  ideal_model?: string;
  blocked_by?: string;
  vision_promoted?: boolean;
  tool_promoted?: boolean;
  promoted_from?: string;
  warnings?: string[];
}

export interface ChatCompletionResponse {
  id: string;
  object: 'chat.completion';
  created: number;
  model: string;
  choices: Array<{
    index: number;
    message: ChatMessage;
    finish_reason: string;
  }>;
  usage: Usage;
  routing?: RoutingInfo;
}

/** Mid-stream error event payload (same envelope as HTTP errors). */
export interface ChatStreamError {
  message?: string;
  type?: string;
  code?: string;
  metadata?: Record<string, unknown>;
}

/** One SSE delta chunk from a streaming completion. */
export interface ChatCompletionChunk {
  id: string;
  object: 'chat.completion.chunk';
  created: number;
  model: string;
  choices: Array<{
    index: number;
    delta: { role?: string; content?: string };
    finish_reason: string | null;
  }>;
  usage?: Usage;
  routing?: RoutingInfo;
}

export const chat = {
  completions: {
    /** Non-streaming chat completion. */
    create: (req: ChatCompletionRequest, opts: { signal?: AbortSignal } = {}) =>
      request<ChatCompletionResponse>('/chat/completions', {
        method: 'POST',
        body: JSON.stringify({ ...req, stream: false }),
        signal: opts.signal,
      }),

    /**
     * Streaming chat completion as an SSE iterator over delta chunks.
     * Pass `opts.signal` so the caller (e.g. a "stop generating" button) can
     * abort the underlying request — which also stops server-side billing.
     */
    stream: async function* (
      req: ChatCompletionRequest,
      opts: { signal?: AbortSignal } = {},
    ): AsyncIterableIterator<ChatCompletionChunk> {
      const url = `${BASE}/chat/completions`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await authToken()}`,
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({ ...req, stream: true }),
        signal: opts.signal,
      });
      if (!resp.ok || !resp.body) {
        const body = await resp.json().catch(() => ({}));
        throw new AridelApiError(
          body?.error?.message ?? `${resp.status} ${resp.statusText}`,
          resp.status,
          body?.error?.code,
        );
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          const data = line.slice(5).trim();
          if (!data || data === '[DONE]') continue;
          let parsed: ChatCompletionChunk | { error?: ChatStreamError };
          try {
            parsed = JSON.parse(data);
          } catch {
            // Skip malformed lines; the SSE stream is best-effort here.
            continue;
          }
          // Mid-stream provider errors arrive as a dedicated error event
          // (same envelope as HTTP errors) — surface them as a thrown error,
          // never as assistant text.
          if ('error' in parsed && parsed.error && !('choices' in parsed)) {
            const err = parsed.error;
            throw new AridelApiError(
              err.message ?? 'The model could not process your request.',
              0,
              err.code,
            );
          }
          yield parsed as ChatCompletionChunk;
        }
      }
    },
  },
};

// ---------------------------------------------------------------------------
// models
// ---------------------------------------------------------------------------

export interface ModelEntry {
  id: string;
  object: 'model';
  created: number;
  owned_by: string;
  /** Human display name; `id` is the harness-safe slug. */
  name?: string;
  description?: string;
}

export const models = {
  list: () => request<{ data: ModelEntry[] }>('/models'),
};

// ---------------------------------------------------------------------------
// insights
// ---------------------------------------------------------------------------

export interface InsightsResponse {
  period: string;
  total_requests: number;
  total_spent_usd: number;
  savings: Record<string, unknown>;
  model_distribution: Array<Record<string, unknown>>;
  counterfactual: Record<string, unknown>;
  [key: string]: unknown;
}

export const insights = (
  params: { period?: string; start?: string; end?: string } = {},
) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null) as [string, string][],
  );
  return request<InsightsResponse>(`/insights${qs.size ? `?${qs}` : ''}`);
};

// ---------------------------------------------------------------------------
// audit-logs
// ---------------------------------------------------------------------------

export interface AuditLogEntry {
  id: string;
  user_id: string;
  timestamp: string;
  endpoint: string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  cost_usd: number;
  [key: string]: unknown;
}

export const auditLogs = {
  list: (params: { cursor?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null) as [string, string][],
    );
    return request<{ data: AuditLogEntry[]; next_cursor?: string }>(
      `/audit-logs${qs.size ? `?${qs}` : ''}`,
    );
  },
};

export const aridel = { chat, models, insights, auditLogs };
